import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import {
  useMixpanel,
  useNotify,
  useServiceAuth,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useDocumentReference } from '@lifeomic/phc-web-toolkit/src/hooks/useDocumentReference';
import FileService from '@lifeomic/phc-web-toolkit/src/services/FileService';
import { CitationClass } from '../types';
import RangeViewModalV2 from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/RangeViewModalV2';
import {
  PRIMARY_SURVEY_ID_SYSTEM,
  QuestionnaireResponseAttachment,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/Attachment';
import { SurveyViewModal } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/modals/SurveyViewModal';
import type {
  MedicalResultCitation,
  DocumentReferenceCitation,
  PubMedPaperCitation,
  QuestionnaireResponseCitation,
  KnowledgeBaseCitation,
} from '../types';
import { getCitationClass } from './utils/getCitationClass';
import { isBrowserPreviewableKnowledgeBaseMime } from './utils/knowledgeBaseCitation';
import { useThrottleFn } from 'react-use';
import QueryString from 'qs';
import {
  reportErrorToBugsnag,
  reportInfoToBugsnag,
} from '../../../../src/util/errorReporting/directReporting';

const messages = defineMessages({
  viewPDFError: {
    id: 'agentChat.chat.viewPDFError',
    defaultMessage:
      'Error loading attachment. Check that you are using All Cohorts and try refreshing the page.',
  },
  unsupportedCitationType: {
    id: 'agentChat.chat.unsupportedCitationType',
    defaultMessage: 'Citation type not supported yet',
  },
  knowledgeBaseFileError: {
    id: 'agentChat.chat.knowledgeBaseFileError',
    defaultMessage:
      'Could not open this knowledge base file. Try again or download from Files.',
  },
});

interface CitationProps {
  // reference string that point to a cited resource
  reference: string;
  patient: fhir.Patient;
  onCitationProcessed: () => void;
}

export const Citation = ({
  reference,
  patient,
  onCitationProcessed,
}: CitationProps) => {
  const mixpanel = useMixpanel();
  const [showCitationModal, setShowCitationModal] = React.useState(false);
  const [medicalResult, setMedicalResult] =
    React.useState<MedicalResultCitation>({
      key: undefined,
      system: undefined,
      resourceId: undefined,
      citationType: 'MedicalResult',
      title: 'Medical Result',
    });
  const [attachmentUrl, setAttachmentUrl] = React.useState<
    string | undefined
  >();
  const [questionnaireModalAttachment, setQuestionnaireModalAttachment] =
    React.useState<QuestionnaireResponseAttachment | null>(null);
  const { retrieveDocumentReference, retrieveAttachment } =
    useDocumentReference();

  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const serviceAuth = useServiceAuth();
  const fileService = React.useMemo(() => new FileService(), []);

  const onCitationProcessedMemo = React.useCallback(() => {
    onCitationProcessed();
  }, [onCitationProcessed]);

  const retrieveAttachmentFromDocRefCitation = React.useCallback(
    async (
      citationClass: DocumentReferenceCitation,
    ): Promise<string | undefined> => {
      const resourceId = citationClass.resourceId;
      if (resourceId) {
        const docRef = await retrieveDocumentReference(resourceId);
        const attachmentData = await retrieveAttachment(docRef);
        return attachmentData?.downloadUrl;
      }
    },
    [retrieveDocumentReference, retrieveAttachment],
  );

  const handleDocumentReferenceClicked = React.useCallback(
    (citationClass: DocumentReferenceCitation) => {
      retrieveAttachmentFromDocRefCitation(citationClass)
        .then(
          url => {
            const query = citationClass.query;
            if (!query) {
              setAttachmentUrl(url);
            } else {
              // query string may have other parameters, such as document type
              // but the url should only have the page parameter
              const { page } = QueryString.parse(query);
              setAttachmentUrl(`${url}#${QueryString.stringify({ page })}`);
            }
          },
          error => {
            console.error(error);
            notify(formatMessage(messages.viewPDFError), 'error');
          },
        )
        .then(onCitationProcessedMemo);
    },
    [
      retrieveAttachmentFromDocRefCitation,
      notify,
      formatMessage,
      onCitationProcessedMemo,
    ],
  );

  const handlePubMedPaperClicked = React.useCallback(
    (citationClass: PubMedPaperCitation) =>
      setAttachmentUrl(
        `https://pubmed.ncbi.nlm.nih.gov/${citationClass.pmid}/`,
      ),
    [],
  );

  const handleKnowledgeBaseClicked = React.useCallback(
    async (kb: KnowledgeBaseCitation) => {
      try {
        const list = await fileService.fetchFiles(serviceAuth)({
          datasetId: kb.projectId,
          name: kb.name,
          pageSize: 1,
        });
        const file = list.data?.items?.find(item => item.name === kb.name);
        if (!file?.id) {
          notify(formatMessage(messages.knowledgeBaseFileError), 'error');
          onCitationProcessedMemo();
          return;
        }
        const res = await fileService.fetchOpenLink(serviceAuth)(file.id);
        const downloadUrl = res.data?.downloadUrl;
        if (!downloadUrl) {
          notify(formatMessage(messages.knowledgeBaseFileError), 'error');
          onCitationProcessedMemo();
          return;
        }
        const effectiveMime = res.data?.contentType || file.contentType;
        if (!isBrowserPreviewableKnowledgeBaseMime(effectiveMime)) {
          reportInfoToBugsnag(
            'Clinician chat: KB citation mime not shown in browser; download fallback',
            {
              metaData: {
                knowledgeBase: {
                  mime: effectiveMime,
                  projectId: kb.projectId,
                  name: kb.name,
                },
              },
            },
          );
          const dl = await fileService.fetchDownloadLink(serviceAuth)(file.id);
          const dlUrl = dl.data?.downloadUrl;
          if (dlUrl) {
            setAttachmentUrl(dlUrl);
          } else {
            notify(formatMessage(messages.knowledgeBaseFileError), 'error');
          }
          onCitationProcessedMemo();
          return;
        }
        const mime = (effectiveMime || '').toLowerCase();
        let url = downloadUrl;
        if (mime === 'application/pdf' && kb.page) {
          url = `${downloadUrl}#${QueryString.stringify({ page: kb.page })}`;
        }
        setAttachmentUrl(url);
        onCitationProcessedMemo();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        reportErrorToBugsnag(err, {
          metaData: {
            knowledgeBase: { projectId: kb.projectId, name: kb.name },
          },
        });
        notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        onCitationProcessedMemo();
      }
    },
    [fileService, serviceAuth, notify, formatMessage, onCitationProcessedMemo],
  );

  const handleCitationClicked = React.useCallback(
    (citationClass: CitationClass) => {
      switch (citationClass.citationType) {
        case 'MedicalResult':
          setMedicalResult(citationClass);
          setShowCitationModal(true);
          onCitationProcessedMemo();
          break;
        case 'DocumentReference':
          handleDocumentReferenceClicked(citationClass);
          break;
        case 'PubMedPaper':
          handlePubMedPaperClicked(citationClass);
          onCitationProcessedMemo();
          break;
        case 'QuestionnaireResponse': {
          const qr = citationClass as QuestionnaireResponseCitation;
          const resource: fhir.QuestionnaireResponse = {
            resourceType: 'QuestionnaireResponse',
            id: qr.resourceId,
            status: 'completed',
          };
          if (qr.primarySurveyId) {
            resource.meta = {
              tag: [
                {
                  system: PRIMARY_SURVEY_ID_SYSTEM,
                  code: qr.primarySurveyId,
                },
              ],
            };
          }
          setQuestionnaireModalAttachment(
            new QuestionnaireResponseAttachment(
              { questionnaireData: { hits: { hits: [] } } },
              resource,
            ),
          );
          onCitationProcessedMemo();
          break;
        }
        case 'KnowledgeBase':
          void handleKnowledgeBaseClicked(
            citationClass as KnowledgeBaseCitation,
          );
          break;
        default:
          notify(formatMessage(messages.unsupportedCitationType), 'warning');
          onCitationProcessedMemo(); // Call this for unsupported types too
      }
      if ('citationType' in citationClass && citationClass.citationType) {
        mixpanel.track('zora_citation_opened', {
          type: citationClass.citationType,
          title: 'title' in citationClass ? citationClass.title : undefined,
        });
      }
    },
    [
      mixpanel,
      onCitationProcessedMemo,
      handleDocumentReferenceClicked,
      handlePubMedPaperClicked,
      handleKnowledgeBaseClicked,
      notify,
      formatMessage,
    ],
  );

  useThrottleFn(
    (
      reference: string,
      showCitationModal: boolean,
      openQuestionnaireAttachment: QuestionnaireResponseAttachment | null,
    ) => {
      if (reference && !showCitationModal && !openQuestionnaireAttachment) {
        const citationClass = getCitationClass(reference);
        handleCitationClicked(citationClass);
      }
    },
    1000,
    [reference, showCitationModal, questionnaireModalAttachment],
  );

  React.useEffect(() => {
    // open attachment url in a new window
    if (attachmentUrl) {
      window.open(attachmentUrl);
    }
  }, [attachmentUrl, setAttachmentUrl]);

  React.useEffect(() => {
    if (medicalResult.key) {
      setShowCitationModal(true);
    }
  }, [medicalResult, setShowCitationModal]);

  return (
    <>
      {showCitationModal && (
        <RangeViewModalV2
          isOpen={showCitationModal}
          onClose={() => {
            // reset medical result to make the same citation clickable
            setMedicalResult({
              key: undefined,
              system: undefined,
              resourceId: undefined,
              citationType: 'MedicalResult',
              title: 'Medical Result',
            });
            setShowCitationModal(false);
          }}
          dimension={{
            codes: [medicalResult],
          }}
          patient={patient}
          initialSelectedResourceId={medicalResult.resourceId}
        />
      )}
      {questionnaireModalAttachment && (
        <SurveyViewModal
          attachment={questionnaireModalAttachment}
          onDismiss={() => setQuestionnaireModalAttachment(null)}
        />
      )}
    </>
  );
};
